import type React from 'react';
import { useCallback } from 'react';

import { OrdDateTimeInput, OrdFormList, OrdIconStandard, TimeZoneInput } from '@ragu-components/ord-components';
import { BooleanInput, maxValue, minValue, useGetIdentity } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { booleanInputStyle } from 'src/lib/globalStyle';

import { dateFormatterFromFCToInput, dateInputValueParser } from './utils';

import type { CalendarEventFields } from '../types';

interface CalendarEventsDateTimeFormProps {
  fields: CalendarEventFields;
}

export const CalendarEventsDateTimeForm = ({ fields }: CalendarEventsDateTimeFormProps): React.ReactElement => {
  const allDayInitial: boolean = useWatch({ name: 'allDay' });
  const allDay: boolean = useWatch({ name: fields.all_day, defaultValue: allDayInitial });
  const dtstart: string = useWatch({ name: fields.dtstart });
  const dtend: string = useWatch({ name: fields.dtend });
  const timezone = useWatch({ name: fields.tzid });
  const { isLoading, data } = useGetIdentity();
  // The readonly dialog disables the form, but pickers and switches stay interactive
  // unless the flag reaches them explicitly
  const { formState } = useFormContext();
  const formDisabled = formState.disabled;

  // 32-bit signed integer Unix timestamp limit (Y2038) https://it.wikipedia.org/wiki/Bug_dell%27anno_2038
  const MAX_DATE = '2038-01-19T03:14:07';

  // Those values are coming from the calendar and used only as default fallback values
  const end: string = useWatch({ name: 'end' });
  const start: string = useWatch({ name: 'start' });

  // Formats the default value
  const defaultFormatter = useCallback((date: string) => dateFormatterFromFCToInput(date, allDay), [allDay]);

  const parser = useCallback(
    (valueFromInput: string | Date | null | undefined) => {
      return dateInputValueParser(valueFromInput, allDay);
    },
    [allDay]
  );

  return (
    <>
      <OrdFormList icon={<OrdIconStandard name='time' />}>
        <OrdDateTimeInput
          defaultValue={dtstart ?? start}
          source={fields.dtstart}
          parse={parser}
          isRequired
          allDay={allDay}
          validate={maxValue(MAX_DATE)}
          timezone={timezone}
          disabled={formDisabled}
        />
        <OrdDateTimeInput
          defaultValue={dtend == null || dtend.length === 0 ? defaultFormatter(end) : defaultFormatter(dtend)}
          source={fields.dtend}
          parse={parser}
          isRequired
          allDay={allDay}
          validate={[minValue(dtstart), maxValue(MAX_DATE)]}
          timezone={timezone}
          disabled={formDisabled}
        />
        <BooleanInput
          defaultValue={allDayInitial}
          sx={booleanInputStyle}
          source={fields.all_day}
          fullWidth
          disabled={formDisabled}
        />
      </OrdFormList>
      {isLoading ? (
        <></>
      ) : (
        <OrdFormList icon={<OrdIconStandard name='language' />}>
          <TimeZoneInput defaultValue={data?.timezone} source={fields.tzid} readOnly={allDay || formDisabled} />
        </OrdFormList>
      )}
    </>
  );
};
